import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import { useHttpClient, type FetchHttpError, type HttpResponse } from '@bonusly/http-client';

import { CreateBookmarkArgs } from '../../../components/bookmark/hooks/use-create-bookmark';
import { BookmarkApiData, BookmarkDTO } from '../../../components/bookmark/lib/entities';
import { useBookmark } from '../../../context';
import { ResourcePath } from '../../../hooks/resource-path';
import { QueryKey } from '../../../lib/query-key';

export interface UpdateBookmarkArgs {
	id: string;
	reference_id: string;
	reference_type: string;
	notes?: string;
	label_ids?: Array<string>;
	suggestions?: Array<string>;
}

export interface UseUpdateBookmarkProps {
	options: UseMutationOptions<HttpResponse<BookmarkApiData>, FetchHttpError, CreateBookmarkArgs, any>;
	id?: string;
}
export const useUpdateBookmark = ({ id, options }: UseUpdateBookmarkProps) => {
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();
	const { setNotes, setBookmark, setLabelsIds, setSuggestions } = useBookmark();
	return useMutation(
		async (data) => {
			let newLabels: Array<string> = [];
			if (data.suggestions && data.suggestions?.length > 0) {
				const responses = data.suggestions?.map((suggestion) =>
					http.post(ResourcePath.Labels, {
						name: suggestion,
					})
				);
				newLabels = (await Promise.all(responses)).map((respose) => respose.data.id);
			}

			if (newLabels.length > 0) {
				queryClient.invalidateQueries([QueryKey.Labels]);
			}

			data.label_ids = [...(data.label_ids || []), ...newLabels];

			return http.patch(`${ResourcePath.Bookmarks}/${id}`, data);
		},
		{
			...options,
			onSuccess: (data, variables, context) => {
				const bookmark = new BookmarkDTO(data.data);
				setBookmark(bookmark);
				setNotes(bookmark?.notes);
				setLabelsIds(bookmark?.labels.map((label) => label.id));
				setSuggestions([]);
				options.onSuccess?.(data, variables, context);

				queryClient.invalidateQueries([QueryKey.Bookmarks, '']);
				queryClient.invalidateQueries([QueryKey.Labels]);
			},
		}
	);
};
